<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();
const store = useStore();
const router = useRouter();

const records = useMapGetter('customRoles/getCustomRoles');
const uiFlags = useMapGetter('customRoles/getUIFlags');
const currentAccountId = useMapGetter('getCurrentAccountId');

const showDeleteConfirmationPopup = ref(false);
const activeRole = ref(null);

onMounted(() => {
  store.dispatch('customRoles/get');
});

const deleteConfirmText = computed(
  () =>
    `${t('SETTINGS.CUSTOM_ROLES.DELETE.CONFIRM.YES')} ${activeRole.value?.name || ''}`
);
const deleteRejectText = computed(
  () =>
    `${t('SETTINGS.CUSTOM_ROLES.DELETE.CONFIRM.NO')} ${activeRole.value?.name || ''}`
);
const deleteMessage = computed(() => ` ${activeRole.value?.name || ''}? `);

const openAddPage = () => {
  router.push({
    name: 'custom_roles_new',
    params: { accountId: currentAccountId.value },
  });
};

const openEditPage = role => {
  router.push({
    name: 'custom_roles_edit',
    params: { accountId: currentAccountId.value, roleId: role.id },
  });
};

const openDeletePopup = role => {
  activeRole.value = role;
  showDeleteConfirmationPopup.value = true;
};

const closeDeletePopup = () => {
  showDeleteConfirmationPopup.value = false;
};

const confirmDeletion = async () => {
  const id = activeRole.value?.id;
  closeDeletePopup();
  try {
    await store.dispatch('customRoles/delete', id);
    useAlert(t('SETTINGS.CUSTOM_ROLES.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(
      error?.message || t('SETTINGS.CUSTOM_ROLES.DELETE.API.ERROR_MESSAGE')
    );
  }
};
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="t('SETTINGS.CUSTOM_ROLES.LOADING')"
    :no-records-found="!records.length"
    :no-records-message="t('SETTINGS.CUSTOM_ROLES.LIST.EMPTY')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="t('SETTINGS.CUSTOM_ROLES.HEADER.TITLE')"
        :description="t('SETTINGS.CUSTOM_ROLES.HEADER.SUBTITLE')"
      >
        <template #actions>
          <Button
            :label="t('SETTINGS.CUSTOM_ROLES.NEW_BUTTON')"
            size="sm"
            @click="openAddPage"
          />
        </template>
      </BaseSettingsHeader>
    </template>

    <template #body>
      <div class="overflow-hidden rounded-lg border border-n-weak">
        <table class="w-full">
          <thead class="bg-n-alpha-black2">
            <tr>
              <th
                class="px-4 py-3 text-left text-xs font-medium text-n-slate-11 uppercase"
              >
                {{ t('SETTINGS.CUSTOM_ROLES.LIST.NAME') }}
              </th>
              <th
                class="px-4 py-3 text-left text-xs font-medium text-n-slate-11 uppercase"
              >
                {{ t('SETTINGS.CUSTOM_ROLES.LIST.DESCRIPTION') }}
              </th>
              <th
                class="px-4 py-3 text-left text-xs font-medium text-n-slate-11 uppercase"
              >
                {{ t('SETTINGS.CUSTOM_ROLES.LIST.PERMISSIONS_COUNT') }}
              </th>
              <th
                class="px-4 py-3 text-right text-xs font-medium text-n-slate-11 uppercase"
              >
                {{ t('SETTINGS.CUSTOM_ROLES.LIST.ACTIONS') }}
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-n-weak">
            <tr v-for="role in records" :key="role.id">
              <td class="px-4 py-3 text-sm text-n-slate-12">{{ role.name }}</td>
              <td class="px-4 py-3 text-sm text-n-slate-11">
                {{ role.description }}
              </td>
              <td class="px-4 py-3 text-sm text-n-slate-11">
                {{ (role.permissions || []).length }}
              </td>
              <td class="px-4 py-3 text-right">
                <div class="inline-flex items-center gap-2">
                  <Button
                    size="xs"
                    faded
                    slate
                    :label="t('SETTINGS.CUSTOM_ROLES.EDIT_BUTTON')"
                    @click="openEditPage(role)"
                  />
                  <Button
                    size="xs"
                    ruby
                    faded
                    :label="t('SETTINGS.CUSTOM_ROLES.DELETE_BUTTON')"
                    @click="openDeletePopup(role)"
                  />
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <woot-delete-modal
        v-model:show="showDeleteConfirmationPopup"
        :on-close="closeDeletePopup"
        :on-confirm="confirmDeletion"
        :title="t('SETTINGS.CUSTOM_ROLES.DELETE.CONFIRM.TITLE')"
        :message="t('SETTINGS.CUSTOM_ROLES.DELETE.CONFIRM.MESSAGE')"
        :message-value="deleteMessage"
        :confirm-text="deleteConfirmText"
        :reject-text="deleteRejectText"
      />
    </template>
  </SettingsLayout>
</template>
